import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EngineSnapshot, WaitingState } from '../engine/stepEngine';
import { StepEngine } from '../engine/stepEngine';
import { bootstrapRuntime } from '../runtime/bootstrap';
import { PlayerRuntime } from '../runtime/playerRuntime';
import { MockPersistenceAdapter } from '../runtime/persistence';
import { ensureDeviceKey } from '../utils/deviceKey';
import { preloadScenarioAssets, type PreloadProgress } from '../utils/scenarioPreload';
import type { ExtendedCertificate, RouteMapHotspotActions, RunState, ScenarioContent } from 'shared';

interface UsePlayerRuntimeState {
  status: 'loading' | 'ready' | 'error';
  error: string | null;
  snapshot: EngineSnapshot | null;
  waiting: WaitingState | null;
  runtime: PlayerRuntime | null;
  isBusy: boolean;
  lastSavedAt: number | null;
  scenarioId: string | null;
  scenario: ScenarioContent | null;
  preload: PreloadProgress | null;
}

export function usePlayerRuntime() {
  const [state, setState] = useState<UsePlayerRuntimeState>({
    status: 'loading',
    error: null,
    snapshot: null,
    waiting: null,
    runtime: null,
    isBusy: false,
    lastSavedAt: null,
    scenarioId: null,
    scenario: null,
    preload: null
  });

  // preview=1 パラメータがある場合、postMessageでシナリオを受け取る
  const isPreviewMode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('preview') === '1';
  }, []);

  const ackSentRef = useRef(false);

  const initializePreviewScenario = useCallback(async (scenario: ScenarioContent, event: MessageEvent) => {
    setState((prev) => ({
      ...prev,
      status: 'loading',
      error: null,
      preload: { total: 0, loaded: 0, failed: [] }
    }));
    const preload = await preloadScenarioAssets(scenario, (progress) => {
      setState((prev) => ({ ...prev, preload: progress }));
    });

    const deviceKey = ensureDeviceKey();
    const engine = new StepEngine(scenario);
    const scenarioTitle =
      typeof (scenario as any)?.meta?.title === 'string'
        ? ((scenario as any).meta?.title as string)
        : 'Preview Scenario';
    const runtime = new PlayerRuntime({
      engine,
      persistence: new MockPersistenceAdapter(scenarioTitle),
      mode: 'mock',
      context: {
        runId: 'preview-run',
        deviceKey,
        scenarioTitle
      }
    });

    setState({
      status: 'ready',
      error: null,
      snapshot: runtime.getSnapshot(),
      waiting: runtime.getWaitingState(),
      runtime,
      isBusy: false,
      lastSavedAt: Date.now(),
      scenarioId: 'preview-scenario',
      scenario,
      preload
    });

    if (!ackSentRef.current) {
      ackSentRef.current = true;
      try {
        event.source?.postMessage({ type: 'PLAYER_PREVIEW_ACK' }, { targetOrigin: event.origin });
      } catch {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    // previewモードの場合、postMessageでシナリオを受け取るまで待機
    if (isPreviewMode) {
      const handleMessage = (event: MessageEvent) => {
        const data = event.data;
        if (!data || typeof data !== 'object') return;
        if (data.type !== 'STUDIO_PREVIEW_SCENARIO') return;

        const scenario = data.scenario as ScenarioContent;
        if (!scenario || !Array.isArray(scenario.nodes)) {
          setState((prev) => ({
            ...prev,
            status: 'error',
            error: 'シナリオデータが不正です',
            isBusy: false
          }));
          return;
        }

        void initializePreviewScenario(scenario, event);
      };

      window.addEventListener('message', handleMessage);
      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }

    // 通常モード: 既存のbootstrapRuntime
    let mounted = true;
    bootstrapRuntime()
      .then(async (result) => {
        if (!mounted) {
          return;
        }
        const { runtime, scenarioId, scenario } = result;
        const preload = await preloadScenarioAssets(scenario, (progress) => {
          if (!mounted) {
            return;
          }
          setState((prev) => ({ ...prev, preload: progress }));
        });
        if (!mounted) {
          return;
        }
        setState({
          status: 'ready',
          error: null,
          snapshot: runtime.getSnapshot(),
          waiting: runtime.getWaitingState(),
          runtime,
          isBusy: false,
          lastSavedAt: Date.now(),
          scenarioId,
          scenario,
          preload
        });
      })
      .catch((error) => {
        if (!mounted) {
          return;
        }
        setState((prev) => ({
          ...prev,
          status: 'error',
          error: error instanceof Error ? error.message : String(error),
          isBusy: false
        }));
      });

    return () => {
      mounted = false;
    };
  }, [isPreviewMode, initializePreviewScenario]);

  const runAction = useCallback(
    async (action: (runtime: PlayerRuntime) => Promise<EngineSnapshot>) => {
      const runtime = state.runtime;
      if (!runtime) {
        return;
      }
      setState((prev) => ({ ...prev, isBusy: true, error: null }));
      try {
        const snapshot = await action(runtime);
        setState((prev) => ({
          ...prev,
          snapshot,
          waiting: runtime.getWaitingState(),
          isBusy: false,
          lastSavedAt: Date.now()
        }));
      } catch (error) {
        // NOTE: persistence失敗（オフライン等）でも、エンジンは進んでいる可能性がある。
        // UIのsnapshotと内部状態がズレないよう、最新snapshotを再取得して表示しつつエラーを出す。
        const latestSnapshot = runtime.getSnapshot();
        setState((prev) => ({
          ...prev,
          snapshot: latestSnapshot,
          waiting: runtime.getWaitingState(),
          isBusy: false,
          error: error instanceof Error ? error.message : String(error)
        }));
      }
    },
    [state.runtime]
  );

  const generateCertificate = useCallback(async (): Promise<ExtendedCertificate> => {
    const runtime = state.runtime;
    if (!runtime) {
      throw new Error('Runtime not initialized');
    }
    return runtime.generateCertificate();
  }, [state.runtime]);

  const actions = useMemo(
    () => ({
      advance: () => runAction((runtime) => runtime.advance()),
      chooseBranch: (goto?: string) => runAction((runtime) => runtime.resolveBranch(goto)),
      submitMinigameResult: (result: Record<string, unknown>) =>
        runAction((runtime) => runtime.submitMinigameResult(result)),
      submitSpotGateAnswer: (answer: string) =>
        runAction((runtime) => runtime.submitSpotGateAnswer(answer)),
      submitFreeInput: (payload: {
        userText: string;
        suggestedRouteKey?: string;
        inputMeta?: {
          charCount: number;
          isSilent?: boolean;
          isTooShort?: boolean;
          isNonsense?: boolean;
        };
      }) => runAction((runtime) => runtime.submitFreeInput(payload)),
      skipFreeInput: (routeKey?: string) => runAction((runtime) => runtime.skipFreeInput(routeKey)),
      completeReward: (endingId?: string) => runAction((runtime) => runtime.completeReward(endingId)),
      completeEnding: (endingId?: string) => runAction((runtime) => runtime.completeEnding(endingId)),
      selectDialogChoice: (choiceIndex: number) =>
        runAction((runtime) => runtime.selectDialogChoice(choiceIndex)),
      selectLineDialogChoice: (choiceIndex?: number) =>
        runAction((runtime) => runtime.selectLineDialogChoice(choiceIndex)),
      submitLineDialogFreeInput: (userText: string) =>
        runAction((runtime) => runtime.submitLineDialogFreeInput(userText)),
      completeLineDialog: () =>
        runAction((runtime) => runtime.completeLineDialog()),
      discoverRouteMapHotspot: (spotId: string, hotspotId: string, hotspotActions?: RouteMapHotspotActions) =>
        runAction((runtime) => runtime.discoverRouteMapHotspot(spotId, hotspotId, hotspotActions)),
      generateCertificate
    }),
    [runAction, generateCertificate]
  );

  // 保存された状態から再開する
  const restoreState = useCallback((savedState: RunState) => {
    const scenario = state.scenario;
    if (!scenario) {
      console.warn('Cannot restore state: scenario not loaded');
      return;
    }

    const deviceKey = ensureDeviceKey();
    const engine = new StepEngine(scenario, savedState);
    const scenarioTitle =
      typeof scenario?.meta?.title === 'string'
        ? scenario.meta.title
        : 'Restored Scenario';
    const runtime = new PlayerRuntime({
      engine,
      persistence: new MockPersistenceAdapter(scenarioTitle),
      mode: 'mock',
      context: {
        runId: 'restored-run',
        deviceKey,
        scenarioTitle
      }
    });

    setState((prev) => ({
      ...prev,
      runtime,
      snapshot: runtime.getSnapshot(),
      waiting: runtime.getWaitingState(),
      lastSavedAt: Date.now()
    }));
  }, [state.scenario]);

  return {
    status: state.status,
    error: state.error,
    snapshot: state.snapshot,
    waiting: state.waiting,
    actions,
    isBusy: state.isBusy,
    mode: state.runtime?.getMode() ?? 'mock',
    context: state.runtime?.getContext(),
    lastSavedAt: state.lastSavedAt,
    preload: state.preload,
    startScreen: state.scenario?.startScreen,
    scenarioId: state.scenarioId,
    scenario: state.scenario,
    restoreState,
    /** Phase A-h: カスタム変数定義（デバッグパネル用） */
    variableDefinitions: state.scenario?.variables?.definitions ?? [],
    /** 手がかり定義（ClueWindow用） */
    clueDefinitions: state.scenario?.clues ?? [],
    /** シナリオUI設定（statusDisplay等） */
    scenarioUi: state.scenario?.ui
  };
}

